import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { faClose, faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useReducer, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../../../app/apiClient";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { decreaseFullScreenLoading, increaseFullScreenLoading } from "../../../appSlice";
import { currentToken } from "../../login/loginSlice";
import { activeComposingEmail, closeEmail, currentComposingEmail } from "../emailSlice";

interface EmailFormState {
    from: string;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    body: string;
}

const initialEmailForm: EmailFormState = { from: "", to: "", cc: "", bcc: "", subject: "", body: "" };

function emailFormReducer(state: EmailFormState, action: { field: keyof EmailFormState; value: string }): EmailFormState {
    return { ...state, [action.field]: action.value };
}

function ComposeMailItem(props: any) {
    let userToken = useAppSelector(currentToken);
    let composingEmailGuid = useAppSelector(currentComposingEmail);
    let isActive = composingEmailGuid === props.email.guid;
    let dispatch = useAppDispatch();

    let [emailForm, dispatchEmailForm] = useReducer(emailFormReducer, initialEmailForm);
    const setEmailField = (field: keyof EmailFormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => dispatchEmailForm({ field, value: e.target.value });

    let [showCcBcc, setShowCcBcc] = useState(false);
    let [isFullScreen, setIsFullScreen] = useState(false);
    // Mirrors Inbox.scss's $tablet breakpoint: below it, compose always fills the
    // viewport (no usable docked-popup size on a phone/tablet screen).
    let [isNarrowViewport, setIsNarrowViewport] = useState(() => window.matchMedia("(max-width: 600px)").matches);

    const sendEmailMessage = useCallback(() => {
        dispatch(increaseFullScreenLoading());
        let payload: any = {
            from: emailForm.from,
            to: emailForm.to,
            subject: emailForm.subject,
            text: emailForm.body,
            html: emailForm.body
        };
        if (emailForm.cc) {
            payload.cc = emailForm.cc;
        }
        if (emailForm.bcc) {
            payload.bcc = emailForm.bcc;
        }
        apiFetch("/api/emails/send", {
            method: "POST",
            token: userToken,
            body: JSON.stringify(payload)
        }).then((data) => {
            return data.json();
        }).then(() => {
            dispatch(closeEmail(props.email.guid));
            dispatch(decreaseFullScreenLoading());
        });
    }, [dispatch, userToken, emailForm, props.email.guid]);

    const toggleFullScreen = () => {
        if (!isActive) {
            dispatch(activeComposingEmail(props.email.guid));
        }
        setIsFullScreen(!isFullScreen);
    }

    useEffect(() => {
        if (!isActive) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isFullScreen) {
                setIsFullScreen(false);
            } else if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                sendEmailMessage();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isActive, isFullScreen, sendEmailMessage]);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 600px)");
        const handleChange = (e: MediaQueryListEvent) => setIsNarrowViewport(e.matches);
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    let showFullScreen = isActive && (isFullScreen || isNarrowViewport);

    let composeWindow = (<div className={"newEmail" + (isActive ? " active" : "") + (showFullScreen ? " fullscreen" : "")} key={props.email.guid}>
        <div className={"emailHeader"} onClick={() => dispatch(activeComposingEmail(props.email.guid))}>
            <div className={"iconsLeft"}>
                <button type="button" className={"iconBtn"} title="Close" onClick={(e) => { e.stopPropagation(); dispatch(closeEmail(props.email.guid)); }}>
                    <FontAwesomeIcon icon={ faClose } />
                </button>
            </div>
            <h1>{emailForm.subject || props.emailHeader || "New Email"}</h1>
            <div className={"iconsRight"}>
                <button type="button" className={"iconBtn expandBtn"} title={isFullScreen ? "Exit full screen" : "Full screen"} onClick={(e) => { e.stopPropagation(); toggleFullScreen(); }}>
                    <FontAwesomeIcon icon={ isFullScreen ? faCompress : faExpand } />
                </button>
                <button type="button" className={"sendBtn"} onClick={(e) => { e.stopPropagation(); sendEmailMessage(); }}>
                    <FontAwesomeIcon icon={ faPaperPlane } /> Send
                </button>
            </div>
        </div>
        <div className={"emailBody"}>
            <div className={"fieldRow"}>
                <span className={"fieldLabel"}>From</span>
                <input value={emailForm.from} placeholder="From Address" onChange={setEmailField("from")} autoCapitalize={"off"} />
            </div>
            <div className={"fieldRow"}>
                <span className={"fieldLabel"}>To</span>
                <input value={emailForm.to} placeholder="To Address" onChange={setEmailField("to")} autoCapitalize={"off"} />
                {!showCcBcc && <span className={"ccBccToggle"} onClick={() => setShowCcBcc(true)}>Cc Bcc</span>}
            </div>
            {showCcBcc && <div className={"fieldRow"}>
                <span className={"fieldLabel"}>Cc</span>
                <input value={emailForm.cc} placeholder="Cc Address" onChange={setEmailField("cc")} autoCapitalize={"off"} />
            </div>}
            {showCcBcc && <div className={"fieldRow"}>
                <span className={"fieldLabel"}>Bcc</span>
                <input value={emailForm.bcc} placeholder="Bcc Address" onChange={setEmailField("bcc")} autoCapitalize={"off"} />
            </div>}
            <div className={"fieldRow"}>
                <span className={"fieldLabel"}>Subject</span>
                <input value={emailForm.subject} placeholder="Subject" onChange={setEmailField("subject")} />
            </div>
            <textarea value={emailForm.body} placeholder="Write your message..." onChange={setEmailField("body")}></textarea>
        </div>
    </div>);

    // Fullscreen is portaled straight to <body> so position:fixed is relative to the
    // viewport rather than .emailsBeingComposed, which is transformed for centering.
    return showFullScreen ? createPortal(composeWindow, document.body) : composeWindow;
}

export default ComposeMailItem;
